import { CallLogRow } from './types';

/**
 * Shared call-log presentation helpers, so the direction icon/label and
 * duration/time formatting don't drift across the customer timeline, the
 * per-customer history, the global calls page, and the report.
 */

export interface CallMeta { icon: string; color: string; label: string }

const META: Record<string, CallMeta> = {
  'inbound-answered':  { icon: 'call_received', color: 'text-emerald-600', label: 'שיחה נכנסת' },
  'inbound-completed': { icon: 'call_received', color: 'text-emerald-600', label: 'שיחה נכנסת' },
  'inbound-missed':    { icon: 'call_missed',   color: 'text-red-500',     label: 'שיחה שלא נענתה' },
  'inbound-canceled':  { icon: 'call_missed',   color: 'text-red-500',     label: 'שיחה שלא נענתה' },
  'inbound-failed':    { icon: 'call_missed',   color: 'text-red-500',     label: 'שיחה שלא נענתה' },
  'inbound-busy':      { icon: 'call_missed',   color: 'text-red-500',     label: 'שיחה שלא נענתה' },
  'inbound-ringing':   { icon: 'call_received', color: 'text-slate-400',   label: 'שיחה נכנסת' },
  'inbound-screened':  { icon: 'block',         color: 'text-slate-400',   label: 'שיחה סוננה' },
  'inbound':           { icon: 'call_received', color: 'text-emerald-600', label: 'שיחה נכנסת' },
  'outbound':          { icon: 'call_made',     color: 'text-primary',     label: 'שיחה יוצאת' },
};

/** Icon/color/label for a call row, by direction + status. */
export function callMeta(c: Pick<CallLogRow, 'direction' | 'status'>): CallMeta {
  if (c.direction === 'outbound') return META['outbound'];
  // Never fall through to the outbound label for an inbound row — an
  // unmapped inbound status must still read as an incoming call.
  return META[`inbound-${c.status}`] || META['inbound'];
}

// Any inbound call the rep never spoke to — missed, screened, or dropped
// before answer (canceled/failed/busy) — counts as "unanswered" for
// grouping + the call-back affordance.
export const UNANSWERED_INBOUND = ['missed', 'screened', 'canceled', 'failed', 'busy'];
export const isMissed = (c: Pick<CallLogRow, 'direction' | 'status'>) =>
  c.direction === 'inbound' && UNANSWERED_INBOUND.includes(c.status);

/** "m:ss" — empty string for a zero-length call. */
export function fmtCallDuration(sec: number): string {
  if (!sec) return '';
  return `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;
}

/** Localized Israel-time date+time for a call. */
export function fmtCallTime(iso: string, opts?: Intl.DateTimeFormatOptions): string {
  return new Date(iso).toLocaleString('he-IL', {
    timeZone: 'Asia/Jerusalem', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
    ...opts,
  });
}
